import { type HttpClient, query, segment } from '../http';

import {
  type CreateGameNightPayload,
  type GameNight,
  type ListGameNightsQuery,
  type ListGameNightsResult,
  type RsvpAnswer,
  type UpdateGameNightPayload,
} from './game-nights.types';

/**
 * Planned games nights: scheduling, guest list and RSVP.
 *
 * Warning: Every mutation answers the **whole night**, counters included.
 * Replace the state with what comes back rather than patching it locally.
 *
 * @param http Transport supplied by the application.
 * @returns The `gameNights` domain of the API client.
 */
export function createGameNightsApi(http: HttpClient) {
  return {
    /**
     * Lists the nights the caller hosts or was invited to.
     *
     * `upcoming` drops cancelled nights — one is no longer something to
     * prepare for; `past` keeps them, being a record.
     *
     * @param params Scope and pagination.
     * @returns One page of nights with the total.
     */
    list: (params: ListGameNightsQuery = {}) =>
      http.get<ListGameNightsResult>(
        `/game-nights${query({
          scope: params.scope,
          page: params.page,
          limit: params.limit,
        })}`,
      ),

    /**
     * Reads one night.
     *
     * @param id The night id.
     * @returns The night, its shortlist and its guest list.
     * @throws 403 when the caller neither hosts it nor was invited, 404 when
     *   it does not exist.
     */
    findOne: (id: string) => http.get<GameNight>(`/game-nights/${segment(id)}`),

    /**
     * Plans a night; the caller hosts it and counts as accepted.
     *
     * Warning: Guests must be household members or accepted friends — an
     * invitation names someone, it cannot reach an arbitrary account.
     *
     * @param payload Title, date, place, shortlist and guests.
     * @returns The created night.
     * @throws 400 when scheduled in the past, 403 when a guest is outside
     *   the circle, 404 when a game is unknown.
     */
    create: (payload: CreateGameNightPayload) =>
      http.post<GameNight>('/game-nights', payload),

    /**
     * Edits a night (host only).
     *
     * @param id The night id.
     * @param payload The fields to change; `gameIds` replaces the shortlist.
     * @returns The updated night.
     * @throws 403 when not the host, 400 when cancelled or moved to the past.
     */
    update: (id: string, payload: UpdateGameNightPayload) =>
      http.patch<GameNight>(`/game-nights/${segment(id)}`, payload),

    /**
     * Cancels a night (host only).
     *
     * Kept rather than deleted: the guests answered, and an evening that
     * vanishes without a trace reads as a bug to whoever had confirmed.
     *
     * @param id The night id.
     * @returns The cancelled night, `canceledAt` set.
     * @throws 403 when not the host, 400 when already cancelled.
     */
    cancel: (id: string) =>
      http.delete<GameNight>(`/game-nights/${segment(id)}`),

    /**
     * Invites one more account (host only).
     *
     * @param id The night id.
     * @param userId The account to invite.
     * @returns The updated night.
     * @throws 403 when not the host or the guest is outside the circle,
     *   409 when already invited.
     */
    invite: (id: string, userId: string) =>
      http.post<GameNight>(`/game-nights/${segment(id)}/guests`, { userId }),

    /**
     * Answers an invitation.
     *
     * Warning: The host cannot decline their own night — cancelling is the
     * gesture that matches the intent (`game_night.error.rsvp.host_cannot_leave`).
     *
     * @param id The night id.
     * @param status The answer.
     * @returns The updated night, counters included.
     * @throws 403 when the caller was not invited.
     */
    respond: (id: string, status: RsvpAnswer) =>
      http.patch<GameNight>(`/game-nights/${segment(id)}/rsvp`, { status }),

    /**
     * Withdraws a guest.
     *
     * One route, two gestures: passing **your own** id leaves the night and
     * needs no right; passing someone else's is host-only.
     *
     * @param id The night id.
     * @param userId The guest to withdraw.
     * @returns The updated night.
     * @throws 400 when targeting the host — cancel the night instead.
     */
    removeGuest: (id: string, userId: string) =>
      http.delete<GameNight>(
        `/game-nights/${segment(id)}/guests/${segment(userId)}`,
      ),
  };
}

export type GameNightsApi = ReturnType<typeof createGameNightsApi>;
